import React, { useState } from "react";
import { Button, Modal, Stack } from "react-bootstrap";
import { Container } from "reactstrap";
import { PageHeader } from "../../../CommonElements/PageTitle";
import { useTranslation } from "react-i18next";
import { SelectCountry } from "@/Component/Form/SelectCountry";
import TableC from "@/CommonElements/TableC";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { useDebouncedSearch } from "@/utils/hooks/useDebouncedSearch";
import { LabelValue } from "@/Component/Value";
import { useOrganizersAndContracts } from "@/services/orgnizerAndContracts/orgnizerAndContracts";
import { HugeiconsIcon } from "@hugeicons/react";
import { ViewIcon } from "@hugeicons/core-free-icons/index";
import { ActionsFooter } from "@/Component/ActionsFooter";
import { DATE_FORMAT, DATE_TIME_FORMAT, permissions } from "@/utils/constants";
import usePermissions from "@/utils/hooks/usePermissions";
import { useSystem } from "@/utils/hooks/useLocation";
import i18n from "@/i18n";
import { formatDate } from "@/utils/helpers";

const OrganizersAndContracts = () => {
  const {
    hasAnyPermission
  } = usePermissions();
  const { from_id } = useSystem();
  const [contract, setContract] = useState(null);
  const { t } = useTranslation();
  const { getLocalizedText, getLocalizedDate } = useLocalizedText();

  const formatScalar = (value) =>
    value == null || value === "" ? "-" : String(value);

  const formatMaybeDate = (value) => {
    if (value == null || value === "") return "-";
    return getLocalizedDate(value, DATE_TIME_FORMAT) || "-";
  };

  const formatContractSubType = (subType) => {
    if (subType == null || subType === "") return "-";
    if (
      typeof subType === "object" &&
      (subType.name_ar != null || subType.name_en != null)
    ) {
      return getLocalizedText(subType.name_ar, subType.name_en) || "-";
    }
    return String(subType);
  };

  const formatCityDisplay = (row) => {
    if (!row) return "-";
    const city = row.city;
    if (city && (city.name_ar != null || city.name_en != null)) {
      return getLocalizedText(city.name_ar, city.name_en) || "-";
    }
    if (row.city_id != null && row.city_id !== "") return String(row.city_id);
    return "-";
  };
  const { searchValue, debouncedValue: search, setSearchValue } = useDebouncedSearch(
    "",
    500
  );
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({});
  const { data: response, isLoading } = useOrganizersAndContracts({
    page: page,
    per_page: 10,
    search: search,
    ...filters,
  });

  const data = response?.data?.data || [];
  const meta = response?.data?.meta || {};

  const columns = [
    {
      name: t("CONTRACT_ID"),
      key: "id",
    },
    {
      name: t("ORGANIZATION_COUNTRY"),
      key: "requester",
      render: (value) => <SelectCountry.Display value={value?.country_id} />,
    },
    {
      name: t("BUILDING_ORGANIZER"),
      key: "requester",
      render: (value) => getLocalizedText(value?.mission_name_ar, value?.mission_name_en) || "-",
    },
    {
      name: t("NATIONALITY"),
      key: "requester",
      render: (value) => <SelectCountry.Display value={value?.nationality_id} />,
    },
    {
      name: t("AFFILIATED_ORGANIZER"),
      key: "requester",
      render: (value) => value.type === "company" ? getLocalizedText(value?.company_name_ar, value?.company_name_en) : "-",
    },
    {
      name: t("CONTRACT_START_DATE"),
      key: "contract_start_date",
      render: (value) => formatDate( value, i18n.language, DATE_FORMAT ),
    },
    {
      name: t("CONTRACT_END_DATE"),
      key: "contract_end_date",
      render: (value) => formatDate( value, i18n.language, DATE_FORMAT ),
    },
    {
      name: t("PILGRIMS_COUNT"),
      key: "number_of_pilgrims",
    },
    hasAnyPermission([permissions.view_basic_packages]) && {
      name: t("ACTIONS"),
      key: "actions",
    },
  ].filter(Boolean);

  const filterFields = [
    {
      name: "id",
      label: t("CONTRACT_ID"),
    },
    {
      name: "mission_id",
      label: t("ORGANIZER"),
      type: "select",
      optionKey: "missions",
      options: (mission) => ({
        value: mission.id,
        label: <LabelValue.Value value={[mission.id, getLocalizedText(mission.name_ar, mission.name_en)].join(" - ")} />,
      }),
    },
    {
      name: "company_id",
      label: t("AFFILIATED_ORGANIZER"),
      type: "select",
      optionKey: "companies",
      options: (company) => ({
        value: company.id,
        label: <LabelValue.Value value={[company.id, getLocalizedText(company.name_ar, company.name_en)].join(" - ")} />,
      }),
    },
    {
      name: "nationality_id",
      label: t("NATIONALITY"),
      type: "select",
      optionKey: "nationalities",
      options: (country) => ({
        value: country.id,
        label: <SelectCountry.Display value={country.id} />,
      }),
    },
    {
      name: "contract_start_date",
      label: t("CONTRACT_START_DATE"),
      type: "date",
    },
    {
      name: "contract_end_date",
      label: t("CONTRACT_END_DATE"),
      type: "date",
    },
  ];

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
    setPage(1);
  };

  const handleFilterApply = (filters) => {
    setFilters(filters);
    setPage(1);
  };

  const getActions = (rowData) => {
    return [
      {
        key: "view",
        label: <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setContract(rowData);
          }}
          className="btn btn-sm btn-outline-primary d-flex align-items-center gap-2 text-nowrap">
          <HugeiconsIcon size={16} icon={ViewIcon} />
          {t("VIEW")}
        </button>,
      },
    ];
  };

  const onClose = () => {
    setContract(null);
  };

  return (
    <div>
      <PageHeader title={t("ORGANIZERS_AND_CONTRACTS")} sticky={false} />
      <Container fluid>
        <TableC
          data={data}
          columns={columns}
          searchValue={searchValue}
          onSearchChange={handleSearchChange}
          filters={filters}
          filterFields={filterFields}
          onFilterApply={handleFilterApply}
          pagination={{
            current: page,
            total: meta?.total || 0,
            pageSize: meta?.per_page || 10,
            onChange: setPage,
          }}
          loading={isLoading}
          actions={getActions}
          skeletonCount={10}
        />

        <Modal
          show={!!contract}
          onHide={onClose}
          centered
          scrollable
          size="md"
        >
          <Modal.Header closeButton>
            <Modal.Title>{t("CONTRACT_DETAILS")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {contract && (
              <Stack gap={3}>
                <div>
                  <div className="text-muted small">{t("CONTRACT_VAT_VALUE")}</div>
                  <div>{formatScalar(contract.vat_value)}</div>
                </div>
                <div>
                  <div className="text-muted small">{t("CONTRACT_AMOUNT")}</div>
                  <div>{formatScalar(contract.amount)}</div>
                </div>
                <div>
                  <div className="text-muted small">
                    {t("CONTRACT_AMOUNT_INCLUDING_VAT")}
                  </div>
                  <div>{formatScalar(contract.amount_inc_vat)}</div>
                </div>
                <div>
                  <div className="text-muted small">{t("CONTRACT_SUB_TYPE")}</div>
                  <div>{formatContractSubType(contract.contract_sub_type)}</div>
                </div>
                <div>
                  <div className="text-muted small">
                    {t("CONTRACT_LAST_UPDATED_AT")}
                  </div>
                  <div>{formatMaybeDate(contract.timestamp)}</div>
                </div>
                <div>
                  <div className="text-muted small">
                    {t("CONTRACT_NUSUK_UPDATED_AT")}
                  </div>
                  <div>{formatMaybeDate(contract.timestamps)}</div>
                </div>
                <div>
                  <div className="text-muted small">{t("CITY")}</div>
                  <div>{formatCityDisplay(contract)}</div>
                </div>
              </Stack>
            )}
          </Modal.Body>
          <ActionsFooter className="px-3">
            <Button onClick={onClose} className="btn-danger btn btn-sm">
              {t("CLOSE")}
            </Button>
          </ActionsFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default OrganizersAndContracts;

